import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import IconButton, {
  IconButtonProps,
} from '@mildang/design-system-legacy/esm/components/molecules/IconButton';
import { css } from '@mildang/styled-system/css';
import { useEffect, useState } from 'react';
import BellOff from '@mildang/icons/react/bell-off';
import notification from '@shared/utils/notification';
import { confirm } from '@mildang/design-system-legacy/esm/components/molecules/dialog/ConfirmDialog';
import Image from '@mildang/design-system-legacy/esm/components/molecules/Image/Image';
import PushNotificationImage from './assets/push_notification.png';
import PCPushNotificationDeniedImage from './assets/pc_push_notification_denied.png';
import AndroidPushNotificationDeniedImage from './assets/android_push_notification_denied.png';
import { useLingui } from '@lingui/react';
import { isIOSEnv } from '@shared/utils/notification/IOSNotification';
import { getRegisterFcmTokenInput } from '@ui/features/mildang-ai/mutations/registerFcmToken/useRegisterFcmToken';
import { RegisterFcmTokenDocument, RegisterFcmTokenMutation, RegisterFcmTokenMutationVariables } from '@ui/features/mildang-ai/mutations/registerFcmToken';
import { useUAParser } from '@ui/hooks/useUAParser';
import { isLikelyTWA } from '@shared/utils/notification/WebPushNotification';
import { useMutation } from '@apollo/client';


export const PushNotificationContent = ({ type }: { type: 'default' | 'denied' | 'android-denied' }) => {
  const imageProps = (() => {
    if (type === 'android-denied') {
      return {
        src: AndroidPushNotificationDeniedImage,
        width: 264,
        height: 166,
      };
    }
    if (type === 'denied') {
      return {
        src: PCPushNotificationDeniedImage,
        width: 264,
        height: 166,
      };
    }
    return {
      src: PushNotificationImage,
      width: 140,
      height: 112,
    };
  })();

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      })}
    >
      <div className={css({ alignSelf: 'center' })}>
        <Image
          alt="푸시 알림 안내"
          src={imageProps.src}
          layout={'fixed'}
          width={imageProps.width}
          height={imageProps.height}
        />
      </div>
      {type === 'default' && (
        <Trans>
          안정적인 채팅 사용을 위해 푸시 알림을 허용해 주세요. 알림 받기 설정시, 화면이 꺼져 있어도 메시지가
          오면 알림을 받을 수 있어요.
        </Trans>
      )}
      {type === 'denied' && (
        <Trans>
          알림이 꺼져 있어 받을 수 없어요. 설정을 변경해야 원활한 사용이 가능합니다.
          <br /> 1. 주소창 좌측 상단의 [설정]
          <br /> 2. 아이콘 클릭 메뉴에서 [알림] 토글 버튼을 켜주세요.
        </Trans>
      )}
      {type === 'android-denied' && (
        <Trans>
          알림이 꺼져 있어 받을 수 없어요. 설정을 변경해야 원활한 사용이 가능합니다.
          <br />
          <br />
          설정 {'>'} 애플리케이션 {'>'} 밀당PT {'>'} 알림 페이지에서 [알림 허용] 토글 버튼을 눌러 알림을
          허용해주세요.
        </Trans>
      )}
    </div>
  );
};

export interface NotificationIconButtonProps extends IconButtonProps {}

export const NotificationIconButton = ({ ...rest }: NotificationIconButtonProps) => {
  const { i18n } = useLingui();
  const { device } = useUAParser();
  const [registerFcmToken] = useMutation<RegisterFcmTokenMutation, RegisterFcmTokenMutationVariables>(RegisterFcmTokenDocument);

  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    notification.getPermissionState().then((permission) => {
      setPermission(permission);
    });

    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' }).then(function (notificationPerm) {
        notificationPerm.onchange = () => {
          setPermission(notificationPerm.state === 'prompt' ? 'default' : notificationPerm.state);
        };
      });
    }
  }, []);

  const handleRequestPermission = async () => {
    await notification.requestPermission().then(async (permission) => {
      if (permission === 'granted') {
        const input = await getRegisterFcmTokenInput();
        if (!input) {
          return;
        }
        registerFcmToken({
          variables: {
            input,
          },
        });
        setPermission(permission);
      }
    });
  };

  if (permission === 'granted') {
    return null;
  }

  if (!notification.supportsNotifications()) {
    return null;
  }

  return (
    <IconButton
      size="medium"
      onClick={async () => {
        try {
          if (permission === 'default') {
            await confirm({
              title: <span>{t(i18n)`채팅 푸시 알림 설정 바로가기`}</span>,
              desc: <PushNotificationContent type="default" />,
              confirmText: <span>{t(i18n)`알림 설정 바로가기`}</span>,
              confirmButtonProps: {
                color: 'primary',
              },
            });
            await handleRequestPermission();
            return;
          }

          if (permission === 'denied') {
            if (isIOSEnv()) {
              await handleRequestPermission();
              return;
            }

            await confirm({
              title: <span>{t(i18n)`푸시 알림이 차단되어 있어요`}</span>,
              desc: <PushNotificationContent type={isLikelyTWA() ? 'android-denied' : 'denied'} />,
              confirmText: <span>{t(i18n)`확인`}</span>,
              confirmButtonProps: {
                color: 'primary',
              },
            });
            return;
          }
        } catch (error) {}
      }}
      {...rest}
    >
      <BellOff />
    </IconButton>
  );
};
